import { Controller, useFormContext } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
import { Input, Spinner } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { InputField, TextareaField } from './form-controls';
import type { ExistenceState } from './use-existence-check';
import type { CustomerWizardValues } from './wizard-form';

/**
 * Step 3 — the contact person. Name, phone and position are required. The phone
 * runs a debounced existence check (owned by the page); a match in the marketing
 * lead pool (`exists_in_contact_sources`) blocks Continue.
 */
export function StepContactInfo({ phone }: { phone: ExistenceState }) {
  const { control, formState } = useFormContext<CustomerWizardValues>();
  const phoneError = formState.errors.customer?.customer_phone?.message;
  const isLead = !!phone.result?.exists_in_contact_sources;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div
        style={{
          display: 'grid',
          gap: 'var(--vn-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}
      >
        <InputField
          name="customer.customer_name"
          label="Contact name"
          placeholder="Full name"
          required
        />

        <Controller
          control={control}
          name="customer.customer_phone"
          render={({ field }) => (
            <Field
              label="Phone number"
              required
              error={phoneError || (isLead ? 'This phone number is already a marketing lead.' : undefined)}
            >
              {(p) => (
                <Input
                  {...p}
                  {...field}
                  type="tel"
                  placeholder="0987654321"
                  invalid={!!phoneError || isLead}
                  endSlot={
                    phone.checking ? (
                      <Spinner size="sm" />
                    ) : isLead ? (
                      <AlertCircle size={16} style={{ color: 'var(--vn-danger-solid)' }} />
                    ) : null
                  }
                />
              )}
            </Field>
          )}
        />

        <InputField
          name="customer.customer_position"
          label="Position"
          placeholder="e.g. IT Manager"
          required
        />

        <InputField
          name="customer.customer_dob"
          label="Date of birth"
          type="date"
        />
      </div>

      <TextareaField
        name="customer.customer_note"
        label="Notes"
        placeholder="Any context about this contact…"
        rows={3}
      />
    </div>
  );
}
